import { useEffect, useState, type ReactNode } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { AlertTriangle, Info, type LucideIcon } from "lucide-react";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { FormInput } from "@/components/forms/FormControls";
import { confirmationSchema } from "@/schemas/common";

interface ConfirmDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  expectedText?: string;
  confirmationLabel?: string;
  confirmLabel?: string;
  busyLabel?: string;
  tone?: "destructive" | "default";
  icon?: LucideIcon;
  onConfirm?: (confirmation: string) => void | Promise<void>;
  testId?: string;
  /** Block content (lists etc.) under the description, which renders as a <p>. */
  children?: ReactNode;
}

export default function ConfirmDialog({ open = true, onOpenChange, title, description, expectedText, confirmationLabel = "Type to confirm", confirmLabel = "Confirm", busyLabel = "Working…", tone = "destructive", icon, onConfirm, testId, children }: ConfirmDialogProps) {
  const Icon = icon ?? (tone === "destructive" ? AlertTriangle : Info);
  const [busy, setBusy] = useState(false);
  const form = useForm({
    resolver: zodResolver(confirmationSchema(expectedText)),
    defaultValues: { confirmation: "" },
    mode: "onChange",
  });
  const matches = expectedText == null || form.formState.isValid;

  useEffect(() => { if (!open) form.reset(); }, [form, open]);

  const confirm = async ({ confirmation }: { confirmation: string }) => {
    if (!matches || busy) return;
    setBusy(true);
    try {
      await onConfirm?.(confirmation.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-[12px] border-dash-border bg-white sm:max-w-lg" data-testid={testId}>
        <Form {...form}>
        {/* React bubbles submit through the portal; don't let it reach a parent form. */}
        <form onSubmit={(e) => { e.stopPropagation(); form.handleSubmit(confirm)(e); }} noValidate>
          <AlertDialogHeader>
            <span className={`mb-2 flex size-10 items-center justify-center rounded-[8px] ${tone === "destructive" ? "bg-dash-rust/10 text-dash-rust" : "bg-primary/10 text-primary"}`} aria-hidden="true"><Icon className="size-5" /></span>
            <AlertDialogTitle className="text-2xl text-dash-ink">{title}</AlertDialogTitle>
            <AlertDialogDescription className="leading-relaxed">{description}</AlertDialogDescription>
          </AlertDialogHeader>
          {children && <div className="mt-4">{children}</div>}
          {expectedText != null && (
            <div className="my-6"><FormInput name="confirmation" label={confirmationLabel} placeholder={expectedText} autoFocus data-testid="delete-deal-confirm-input" required /></div>
          )}
          <AlertDialogFooter className="mt-6 gap-2 sm:gap-2">
            <AlertDialogCancel className="!rounded-[8px]" data-testid="delete-deal-cancel">Cancel</AlertDialogCancel>
            <Button type="submit" variant={tone === "destructive" ? "destructive" : "default"} className="!rounded-[8px]" disabled={!matches} loading={busy} data-testid="delete-deal-confirm">{confirmLabel}</Button>
          </AlertDialogFooter>
        </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
